import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Global Conflict (c. 1900–Present)",
  examWeight: "15–20%",
  focus: "How internal and external factors caused the collapse of older empires, led to global wars, and shifted the balance of power.",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Shifting Power After 1900",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Collapse of the Ottoman Empire ("The Sick Man of Europe")</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📉 Causes of Decline</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Economic:</strong> Failed to modernize/industrialize; fierce competition from European trade.</li>
                  <li><strong>Political:</strong> Increasing nationalism among diverse populations (Greeks, Arabs, Armenians) led to independence movements.</li>
                  <li><strong>External:</strong> The "Tanzimat Reforms" failed to stop European encroachment.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">⚔️ The End</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Young Turks:</strong> A reformist group advocated for a constitutional government and Turkification (Turkish nationalism), which alienated Arab minorities.</li>
                  <li>Aligned with Germany in WWI; dissolved in 1922.</li>
                  <li><strong>Result:</strong> Rise of the Republic of Turkey under <strong>Mustafa Kemal Ataturk</strong> (westernized/secularized the nation).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇷🇺 Collapse of the Russian Empire</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📉 Causes of Decline</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Slow industrialization compared to the West.</li>
                  <li>Embarrassing defeat in the <strong>Russo-Japanese War (1905)</strong>.</li>
                  <li><strong>Bloody Sunday (1905):</strong> Massacre of peaceful protesters weakened the Tsar's legitimacy.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">⚡ The Revolution (1917)</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>WWI devastated Russia's economy and army.</li>
                  <li><strong>The Bolsheviks (Communists)</strong>, led by <strong>Vladimir Lenin</strong>, seized power, promising "Peace, Land, and Bread."</li>
                  <li><strong>Result:</strong> Establishment of the Soviet Union (USSR), the first communist state.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇨🇳 Collapse of the Qing Dynasty (China)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">📉 Causes of Decline</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Ethnic Tension:</strong> The Qing were Manchus ruling over the Han Chinese majority.</li>
                  <li><strong>External Pressure:</strong> Spheres of Influence (European/Japanese economic control) and the Opium Wars.</li>
                  <li><strong>Internal Rebellion:</strong> The Boxer Rebellion (anti-foreign) weakened the state.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">⚡ The Revolution (1911)</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Led by <strong>Sun Yat-sen</strong>. He promoted the <strong>Three People's Principles</strong>: Nationalism, Democracy, and Livelihood.</li>
                  <li><strong>Result:</strong> End of 2,000 years of dynastic rule; establishment of the Republic of China (though it quickly fell into warlordism).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇲🇽 The Mexican Revolution (1910–1920)</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Context" 
                definition="Dictator Porfirio Díaz allowed foreign investors (US) to control Mexico's resources, leaving 97% of land owned by the wealthy 1%."
              />
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">👥 Key Figures</h4>
                <p className="text-sm text-slate-700 mt-1">Pancho Villa and Emiliano Zapata (leaders of the peasantry).</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">✅ Result</h4>
                <p className="text-sm text-slate-700 mt-1">Díaz was overthrown. The <strong>Constitution of 1917</strong> established land redistribution (taking from wealthy, giving to peasants), voting rights, and public education.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Causes of World War I",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 The M.A.I.N. Causes</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">⚔️ M - Militarism</h4>
                <p className="text-sm text-slate-700 mt-1">Massive buildup of weapons (industrialization) and glorification of war. Britain and Germany engaged in a naval arms race.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-cyan-900">🤝 A - Alliances</h4>
                <p className="text-sm text-slate-700 mt-1">Secret treaties meant a local conflict would become global.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Triple Entente:</strong> Britain, France, Russia.</li>
                  <li><strong>Triple Alliance:</strong> Germany, Austria-Hungary, Italy.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-green-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-teal-900">🌍 I - Imperialism</h4>
                <p className="text-sm text-slate-700 mt-1">Competition for colonies in Africa and Asia created rivalry.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇪🇺 N - Nationalism</h4>
                <p className="text-sm text-slate-700 mt-1">Intense pride in one's nation.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Self-Determination:</strong> The idea that people of the same ethnicity should have their own state. This destabilized the Austro-Hungarian and Ottoman Empires (e.g., Serbs wanting independence).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💥 The Spark</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📅 June 28, 1914</h4>
                <p className="text-sm text-slate-700 mt-1">Archduke Franz Ferdinand (heir to Austria-Hungary) was assassinated in Sarajevo by <strong>Gavrilo Princip</strong>, a Serbian nationalist (member of the Black Hand).</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">🌊 The Domino Effect</h4>
                <p className="text-sm text-slate-700 mt-1">Austria declared war on Serbia → Russia mobilized to help Serbia → Germany mobilized to help Austria → France/Britain joined Russia.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: Conducting World War I",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔫 Technological Warfare</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">New technology turned the war into a defensive stalemate with high casualties.</p>
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">🕳️ Trench Warfare</h4>
                <p className="text-sm text-slate-700 mt-1">Soldiers dug thousands of miles of trenches (Western Front).</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">💣 New Weapons</h4>
                <p className="text-sm text-slate-700 mt-1">Machine guns, poison gas (mustard/chlorine), tanks, airplanes, U-boats (submarines).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Total War</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏭 Government Control</h4>
                <p className="text-sm text-slate-700 mt-1">Governments took control of economies (rationing food, setting production quotas).</p>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                <h4 className="font-bold text-violet-900">📢 Propaganda</h4>
                <p className="text-sm text-slate-700 mt-1">Used to demonize the enemy, encourage enlistment, and keep morale up.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">👩‍🏭 Women</h4>
                <p className="text-sm text-slate-700 mt-1">Took over men's jobs in factories; this economic contribution helped women gain voting rights after the war.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Global Scope</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🌏 Imperial Powers Used Colonial Subjects</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Sepoys (India):</strong> Fought for Britain in hopes of gaining independence.</li>
                  <li><strong>West Africans:</strong> Fought for France.</li>
                  <li><strong>ANZAC:</strong> Australian/New Zealand troops fought at Gallipoli.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🇺🇸 US Entry (1917)</h4>
                <p className="text-sm text-slate-700 mt-1">Caused by unrestricted submarine warfare (Lusitania) and the Zimmermann Telegram. This tipped the balance in favor of the Allies.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: The Economy in the Interwar Period",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 The Great Depression (1929)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🔴 Causes</h4>
                <p className="text-sm text-slate-700 mt-1">US Stock Market Crash, overproduction of goods, banking failures.</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">🌐 Global Impact</h4>
                <p className="text-sm text-slate-700 mt-1">Since the US was bankrolling Europe (Dawes Plan), the US crash caused a global ripple effect. Global trade dropped.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Government Responses</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇺🇸 United States (Liberalism)</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Franklin D. Roosevelt's New Deal:</strong> Based on <strong>Keynesian Economics</strong> (John Maynard Keynes argued governments should deficit spend to stimulate the economy).</li>
                  <li>Created jobs (public works), social security, and banking regulations.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇷🇺 Soviet Union (Strict Control)</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Five-Year Plans:</strong> Stalin's plan to rapidly industrialize.</li>
                  <li><strong>Collectivization:</strong> Government seizure of farmland to create state-owned farms. Resulted in the death of millions (famine), but successfully industrialized the USSR while the West starved.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇮🇹 🇩🇪 Fascist Italy & Germany (Corporatism)</h4>
                <p className="text-sm text-slate-700 mt-1">Governments allowed private property but managed the economy closely to support the military and state goals.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-5",
      title: "Topic 7.5: Unresolved Tensions After World War I",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗺️ The Mandate System</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed mb-4">
                The League of Nations took colonies from the Central Powers (Germany/Ottomans) and gave them to Britain and France as "Mandates" (territories to be "guided" toward independence).
              </p>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">⚠️ Reality</h4>
                <p className="text-sm text-slate-700 mt-1">It was colonization by another name.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Middle East:</strong> Britain took Palestine/Iraq; France took Syria/Lebanon.</li>
                  <li><strong>Pan-Arabism:</strong> Anger over the Mandate System fueled Arab nationalism.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✊ Anti-Imperialist Movements</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed mb-4">
                Colonial peoples expected self-determination after fighting in WWI. When they didn't get it, resistance grew.
              </p>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇮🇳 India</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Indian National Congress</strong> and <strong>Mahatma Gandhi</strong> used civil disobedience (Salt March) to protest British rule.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <h4 className="font-bold text-emerald-900">🇰🇷 Korea</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>March 1st Movement (1919)</strong> against Japanese colonial rule.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-cyan-900">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>May Fourth Movement (1919)</strong> protesting the Treaty of Versailles giving Chinese land to Japan.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-6",
      title: "Topic 7.6: Causes of World War II",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 Treaty of Versailles (1919)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">⚖️ Harsh Terms</h4>
                <p className="text-sm text-slate-700 mt-1">Punished Germany severely (War Guilt Clause, massive reparations, loss of military/land).</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">😠 Resentment</h4>
                <p className="text-sm text-slate-700 mt-1">Created resentment that Hitler exploited.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👹 Rise of Totalitarianism/Fascism</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🔴 Fascism</h4>
                <p className="text-sm text-slate-700 mt-1">Authoritarian, nationalist, militaristic, anti-communist.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Italy:</strong> Benito Mussolini.</li>
                  <li><strong>Germany:</strong> Adolf Hitler (Nazi Party). Blamed Jews/minorities for Germany's problems (anti-Semitism).</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                <h4 className="font-bold text-violet-900">🏛️ Totalitarianism</h4>
                <p className="text-sm text-slate-700 mt-1">State controls all aspects of life.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Aggressive Expansion</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border-l-4 border-gray-400">
                <h4 className="font-bold text-gray-900">🇩🇪 Germany</h4>
                <p className="text-sm text-slate-700 mt-1">Sought <strong>Lebensraum</strong> (living space). Remilitarized the Rhineland, annexed Austria and the Sudetenland.</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">🇮🇹 Italy</h4>
                <p className="text-sm text-slate-700 mt-1">Invaded Ethiopia.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇯🇵 Japan</h4>
                <p className="text-sm text-slate-700 mt-1">Invaded Manchuria (1931) and China (1937) for resources.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Failure of Appeasement</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">⚠️ Policy of Appeasement</h4>
                <p className="text-sm text-slate-700 mt-1">Britain and France followed a policy of <strong>Appeasement</strong> (giving in to Hitler to avoid war), famously at the <strong>Munich Conference</strong>.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">💥 The Result</h4>
                <p className="text-sm text-slate-700 mt-1">Hitler broke promises and invaded Poland in 1939, starting WWII.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-7",
      title: "Topic 7.7: Conducting World War II",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Ideological Conflict</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🔴 Axis Powers</h4>
                <p className="text-sm text-slate-700 mt-1">Germany, Italy, Japan (Fascist/Authoritarian).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔵 Allies</h4>
                <p className="text-sm text-slate-700 mt-1">Great Britain (Democracy), USA (Democracy), USSR (Communist). <strong>Note:</strong> The US and USSR were united only by a common enemy.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Strategies and Technology</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-bold text-yellow-900">⚡ Germany: Blitzkrieg</h4>
                <p className="text-sm text-slate-700 mt-1">Lightning War—fast-moving tanks and planes.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">🛩️ Japan: Kamikaze Attacks</h4>
                <p className="text-sm text-slate-700 mt-1">Suicide attacks by pilots.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">💣 Allies: Firebombing & Atomic Bomb</h4>
                <p className="text-sm text-slate-700 mt-1">Firebombing (Dresden, Tokyo) and the Atomic Bomb (Hiroshima, Nagasaki).</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">👩‍🏭 Total War</h4>
                <p className="text-sm text-slate-700 mt-1">Mobilized all civilians (women in factories like "Rosie the Riveter").</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Key Turning Points</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🇪🇺 European Theater</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Battle of Stalingrad:</strong> USSR turns tide against Germany.</li>
                  <li><strong>D-Day:</strong> Allies retake France.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                <h4 className="font-bold text-indigo-900">🌊 Pacific Theater</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Battle of Midway:</strong> US destroys Japanese carriers.</li>
                  <li><strong>Island Hopping Strategy:</strong> Gradual advance toward Japan.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-8",
      title: "Topic 7.8: Mass Atrocities After 1900",
      content: (
        <div className="space-y-6">
          <ImportantNote>
            The 20th century saw the rise of extremist groups using state power to attempt the destruction of specific populations (Genocide).
          </ImportantNote>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇦🇲 The Armenian Genocide (1915)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📋 Details</h4>
                <p className="text-sm text-slate-700 mt-1">Ottoman government accused Christian Armenians of colluding with Russia. Forced deportation and starvation led to the deaths of 600k–1.5 million Armenians.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕯️ The Holocaust (1941–1945)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">📋 Details</h4>
                <p className="text-sm text-slate-700 mt-1">Nazi "Final Solution" to kill Jews, Roma, disabled people, and LGBTQ+ individuals. Used industrial methods (gas chambers/camps). 6 million Jews murdered.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇰🇭 Cambodian Genocide (1975–1979)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">📋 Details</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Pol Pot</strong> and the <strong>Khmer Rouge</strong> (Communists) attempted to create an agrarian utopia. Targeted intellectuals, city dwellers, and dissenters. ~2 million killed (Killing Fields).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇷🇼 Rwandan Genocide (1994)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📋 Details</h4>
                <p className="text-sm text-slate-700 mt-1">Ethnic conflict between <strong>Hutus</strong> (majority) and <strong>Tutsis</strong> (minority). Hutu extremists killed 800k Tutsis in 100 days while the UN failed to intervene effectively.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Other Atrocities</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-bold text-yellow-900">🇺🇦 Ukraine (Holodomor)</h4>
                <p className="text-sm text-slate-700 mt-1">Stalin's artificial famine killed millions of kulaks (peasants).</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-orange-900">🇨🇳 Nanjing Massacre</h4>
                <p className="text-sm text-slate-700 mt-1">Japanese troops mass raped and killed Chinese civilians (1937).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-9",
      title: "Topic 7.9: Causation in Global Conflict",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Continuity and Change</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">✨ Changes</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Global balance of power shifted from Europe to the <strong>USA and USSR</strong>.</li>
                  <li>Land-based empires (Ottoman, Qing, Russian) dissolved.</li>
                  <li>Colonial empires (British, French) weakened, leading to decolonization.</li>
                  <li>Women gained political rights (suffrage) due to war participation.</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔁 Continuities</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Use of nationalism to mobilize populations.</li>
                  <li>Persistence of geopolitical rivalry (shifted from Imperial rivalry to Cold War rivalry).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Global Power Shift</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">📊 The Shift</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>Europe was physically and economically devastated by two world wars.</li>
                  <li>The US emerged as an economic superpower (industrial capacity intact).</li>
                  <li>The USSR emerged as a military superpower (despite massive casualties).</li>
                  <li>This set the stage for <strong>Unit 8: The Cold War</strong>.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit7Notes;